import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Scene } from "@babylonjs/core/scene";
import { AppDispatch, RootState } from "app/store";
import { SpinModal } from "common/elements";
import { actions } from "features/builder/slice";
import { PieceMilestone } from "features/builder/types";
import { recordEditHistory } from "features/builder/hook";
import { captureBuilderThumbnail } from "features/builder/captureThumbnail";
import { BuilderProvider, useBuilderScene } from "features/builder/context/BuilderContext";
import { AssetBar } from "./AssetBar";
import { BuilderScene } from "./scene";
import { BuilderHeader } from "./BuilderHeader";
import { ToolController } from "./ToolController";
import { BuilderPlaceHint } from "./BuilderPlaceHint";
import { PublishStepsPanel } from "./PublishStepsPanel";
import { BuilderPieceDetail } from "./BuilderPieceDetail";
import { SelectedModelPanel } from "./SelectedModelPanel";

// captureRequested フラグを監視し、シーンが生きているうちに 3D マップを撮影してから一覧へ遷移する。
// （アンマウント時に撮ると engine 破棄と競合して失敗するため、遷移前にここで確実に撮る）
const BuilderCapture = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const scene = useBuilderScene();
  const uid = useSelector((state: RootState) => state.user.active?.uid);
  const pieceId = useSelector((state: RootState) => state.builder.editingBuilderMapId);
  const requested = useSelector((state: RootState) => state.builder.captureRequested);

  useEffect(() => {
    if (!requested) return;
    let cancelled = false;
    (async () => {
      await captureBuilderThumbnail(scene, uid, pieceId).catch(() => undefined);
      if (cancelled) return;
      // 閉じたときの区切りを記録に残す
      dispatch(recordEditHistory(PieceMilestone.CLOSE));
      dispatch(actions.clearCaptureRequest());
      navigate("/courses");
    })();
    return () => {
      cancelled = true;
    };
    // requested の立ち上がりでのみ実行（scene/uid/pieceId は実行時に最新を参照）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requested]);

  if (!requested) return null;
  return <SpinModal />;
};

export const Canvas = () => {
  const navigate = useNavigate();
  const [scene, setScene] = useState<Scene | null>(null);
  // ピース情報モーダルはヘッダー・公開準備パネルの双方から開くため、ここに集約
  const [showPieceInfo, setShowPieceInfo] = useState(false);

  return (
    <BuilderProvider>
      <BuilderHeader onEditInfo={() => setShowPieceInfo(true)} />
      <ToolController />
      <SelectedModelPanel />
      <PublishStepsPanel
        // ヘッダーの「テスト」と同じく、テストとして開く
        // （?from=test が無いと クリア画面が「いちらんに もどる」になる）
        onTest={() => navigate("preview?from=test")}
        onEditInfo={() => setShowPieceInfo(true)}
      />
      <AssetBar />
      <BuilderScene scene={scene} setScene={setScene} />
      <BuilderPieceDetail open={showPieceInfo} onClose={() => setShowPieceInfo(false)} />
      <BuilderPlaceHint />
      <BuilderCapture />
    </BuilderProvider>
  );
};
